import { ApiError } from './client.js';
import { applyMove, createGame } from './game.js';
import { toHex, verifyCommitment } from './hash.js';
import { classifyConfig, configFor } from './presets.js';
function randomSeed() {
    return toHex(globalThis.crypto.getRandomValues(new Uint8Array(32)));
}
/**
 * Backoff between automatic retries after a network failure, not a rejection.
 *
 * Retrying never blocks anything the player is looking at — the outcome is
 * reported from the local engine the moment the board is finished, and the
 * server's verdict arrives later as its own update — so this can afford to be
 * patient. The last delay repeats for as long as the game stays unsent.
 */
const RETRY_DELAYS_MS = [2_000, 5_000, 12_000, 30_000, 60_000];
/** Unsent games older than this are dropped: the server will not take them. */
const PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Cap on the unsent queue, so a long offline session cannot grow without bound. */
const PENDING_MAX_ENTRIES = 20;
/**
 * A client without a key can still read leaderboards, but it has no identity to
 * attach a score to — so as far as a *game* is concerned, it is offline.
 */
function canRank(client) {
    return client !== null && client.hasToken;
}
export class GameSession {
    listeners = new Set();
    now;
    client;
    snapshot;
    gameId = null;
    commitment = null;
    custom;
    /** Clicks made while the server was still being talked to. */
    queued = [];
    /** Bumped per deal, so a stale response cannot replace a newer board. */
    generation = 0;
    /** Finished games still waiting on the server, oldest first. */
    pending = [];
    store;
    flushing = false;
    retryTimer = null;
    constructor(options = {}) {
        this.client = options.client ?? null;
        this.now = options.now ?? Date.now;
        this.store = options.store ?? null;
        this.pending = this.loadPending();
        const config = configFor('beginner');
        this.snapshot = {
            phase: 'idle',
            game: createGame(config, ''),
            difficulty: 'beginner',
            ranked: false,
            offline: !canRank(this.client),
            startedAt: null,
            result: null,
            error: null,
            pendingSubmission: this.pending.length > 0,
        };
        // A win left over from a previous visit goes out as soon as there is
        // somewhere to send it.
        if (this.pending.length > 0)
            this.scheduleFlush(0);
    }
    /** Reads the durable queue, dropping anything too old to still be accepted. */
    loadPending() {
        if (!this.store)
            return [];
        let stored;
        try {
            stored = this.store.read();
        }
        catch {
            return [];
        }
        const cutoff = this.now() - PENDING_MAX_AGE_MS;
        const fresh = stored.filter((entry) => typeof entry?.gameId === 'string' &&
            Array.isArray(entry.moves) &&
            entry.moves.length > 0 &&
            typeof entry.finishedAt === 'number' &&
            entry.finishedAt > cutoff);
        if (fresh.length !== stored.length)
            this.savePending(fresh);
        return fresh;
    }
    savePending(pending) {
        try {
            this.store?.write(pending);
        }
        catch {
            // A full or unavailable store is not worth failing a game over; the
            // queue still works in memory for the rest of this page's life.
        }
    }
    /** Arrow property so React's `useSyncExternalStore` sees a stable reference. */
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
    getSnapshot = () => this.snapshot;
    /**
     * Swaps the client, typically because the player just signed in or out.
     *
     * Crucially this does *not* touch the current game's `offline` flag. A board
     * belongs to whoever dealt it: one handed out while signed out has no server
     * game behind it, so a win on it can never be submitted no matter what key
     * arrives afterwards. Claiming otherwise used to lose the win silently —
     * `submit` bailed for want of a game id, and the offline notice had already
     * been switched off.
     *
     * If nothing has happened on the board yet, re-deal so signing in takes
     * effect immediately.
     */
    setClient(client) {
        const rankableBefore = canRank(this.client);
        this.client = client;
        // Signing in is also the moment anything stuck in the queue finally has
        // somewhere to go.
        if (canRank(client))
            this.scheduleFlush(0);
        if (canRank(client) === rankableBefore)
            return;
        if (this.snapshot.phase === 'ready') {
            void this.newGame(this.snapshot.difficulty, this.custom);
        }
    }
    patch(changes) {
        this.snapshot = { ...this.snapshot, ...changes };
        for (const listener of this.listeners)
            listener();
    }
    /** Deals a fresh board. Any game still in progress is abandoned. */
    async newGame(difficulty, custom) {
        // Switching difficulty twice in quick succession leaves two deals in
        // flight; without this the slower one lands last and silently replaces the
        // board the player actually asked for.
        const generation = ++this.generation;
        const current = () => this.generation === generation;
        this.custom = custom;
        const localConfig = configFor(difficulty, custom);
        this.gameId = null;
        this.commitment = null;
        this.queued = [];
        this.patch({
            phase: 'dealing',
            game: createGame(localConfig, ''),
            difficulty,
            ranked: false,
            startedAt: null,
            result: null,
            error: null,
            // A game still waiting to be saved stays flagged across a new deal —
            // the board changed, the unsent win did not.
            pendingSubmission: this.pending.length > 0,
        });
        const client = this.client;
        if (!canRank(client) || !client) {
            // Offline play: the board is ours, and so is the seed.
            this.patch({
                phase: 'ready',
                game: createGame(localConfig, randomSeed()),
                offline: true,
                ranked: false,
            });
            this.drainQueued();
            return;
        }
        try {
            const deal = await client.createGame(difficulty, custom);
            if (!current())
                return;
            this.gameId = deal.gameId;
            this.commitment = deal.commitment;
            this.patch({
                phase: 'ready',
                game: createGame(deal.config, ''),
                difficulty: deal.difficulty,
                ranked: deal.ranked,
                offline: false,
            });
            this.drainQueued();
        }
        catch (error) {
            if (!current())
                return;
            // A dead server should never mean a dead game.
            this.patch({
                phase: 'ready',
                game: createGame(localConfig, randomSeed()),
                difficulty: classifyConfig(localConfig),
                ranked: false,
                offline: true,
                error: `${describe(error)} Playing offline — this game will not be ranked.`,
            });
            this.drainQueued();
        }
    }
    async reveal(x, y) {
        const { phase } = this.snapshot;
        if (phase === 'ready')
            return this.openBoard(x, y);
        if (phase === 'dealing' || phase === 'opening')
            return this.enqueue('reveal', x, y);
        if (phase !== 'playing')
            return;
        this.play({ t: this.elapsed(), kind: 'reveal', x, y });
    }
    /**
     * Returns whether the flag actually toggled — false for, say, a cell
     * that's already open. The caller uses this to skip the flag sound/haptic
     * for an attempt that didn't do anything, rather than playing them for
     * every tap regardless of whether it was legal.
     */
    flag(x, y) {
        const { phase } = this.snapshot;
        if (phase === 'dealing' || phase === 'opening') {
            this.enqueue('flag', x, y);
            return false;
        }
        if (phase !== 'playing')
            return false;
        return this.play({ t: this.elapsed(), kind: 'flag', x, y });
    }
    chord(x, y) {
        const { phase } = this.snapshot;
        if (phase === 'dealing' || phase === 'opening')
            return this.enqueue('chord', x, y);
        if (phase !== 'playing')
            return;
        this.play({ t: this.elapsed(), kind: 'chord', x, y });
    }
    /**
     * Holds on to clicks made while the server is still being talked to.
     *
     * Dealing a board and opening it are the only pauses in a game, and on a cold
     * worker either can take a second or two. Dropping input during them is what
     * makes the whole app feel dead — you click, and nothing happens.
     */
    enqueue(kind, x, y) {
        if (this.queued.length >= 16)
            return;
        this.queued.push({ kind, x, y });
    }
    drainQueued() {
        const queued = this.queued;
        this.queued = [];
        for (let i = 0; i < queued.length; i++) {
            const move = queued[i];
            const phase = this.snapshot.phase;
            // A reveal on a freshly dealt board is the opening move, which has to go
            // through the server. Park the rest; `openBoard` drains them afterwards.
            if (phase === 'ready' && move.kind === 'reveal') {
                this.queued = queued.slice(i + 1);
                void this.openBoard(move.x, move.y);
                return;
            }
            if (phase !== 'playing')
                break;
            this.play({ t: this.elapsed(), ...move });
        }
    }
    elapsed() {
        const { startedAt, game } = this.snapshot;
        if (startedAt === null)
            return 0;
        // A wall clock can step backwards — an NTP correction, a laptop coming
        // out of sleep, a phone changing network time. `applyMove` rejects any
        // move whose timestamp precedes the previous one, so a backwards step
        // used to show up as clicks that silently did nothing for as long as the
        // clock took to catch up. Never hand the engine a time earlier than one
        // it has already accepted.
        return Math.max(game.elapsedMs, this.now() - startedAt);
    }
    /**
     * The opening click — the one moment we talk to the server mid-game.
     *
     * We hand over the coordinates, get the seed back, and check it against the
     * commitment we were given before any of this started. From here on the
     * game is entirely local and instant.
     */
    async openBoard(x, y) {
        const { game, offline } = this.snapshot;
        const client = this.client;
        const gameId = this.gameId;
        if (offline || !client || !gameId) {
            const startedAt = this.now();
            this.patch({ phase: 'playing', startedAt });
            this.play({ t: 0, kind: 'reveal', x, y }, game);
            this.drainQueued();
            return;
        }
        this.patch({ phase: 'opening' });
        try {
            const { seed } = await client.startGame(gameId, x, y);
            if (this.commitment && !(await verifyCommitment(seed, this.commitment))) {
                this.patch({
                    phase: 'ready',
                    error: 'The server sent a board that does not match its commitment. Refusing to play it.',
                });
                return;
            }
            // Nothing has happened on the board yet, so swapping in the real seed
            // here loses no state.
            const seeded = createGame(game.config, seed);
            const startedAt = this.now();
            this.patch({ phase: 'playing', startedAt, error: null });
            this.play({ t: 0, kind: 'reveal', x, y }, seeded);
            this.drainQueued();
        }
        catch (error) {
            // The opening click is the one moment the game depends on the network,
            // and losing it used to mean the click simply vanished: back to
            // "ready", an error toast, click again — on the exact connection least
            // likely to do better second time. A network failure instead drops
            // this board to offline play and opens the cell the player actually
            // clicked. The run stops being rankable, which is the honest outcome
            // (no server seed, nothing to verify against), but the game continues
            // rather than stalling.
            //
            // A *server* answer — a 409 on an already-started game, an expired
            // token — is a different thing: it means this board is genuinely no
            // longer playable, and silently swapping in a local one would hide
            // that. Those still return to "ready" with the message.
            const networkFailure = error instanceof ApiError && error.status === 0;
            if (!networkFailure) {
                this.queued = [];
                this.patch({ phase: 'ready', error: describe(error) });
                return;
            }
            this.gameId = null;
            this.commitment = null;
            const local = createGame(game.config, randomSeed());
            const startedAt = this.now();
            this.patch({
                phase: 'playing',
                game: local,
                startedAt,
                offline: true,
                ranked: false,
                error: `${describe(error)} Playing offline — this game will not be ranked.`,
            });
            this.play({ t: 0, kind: 'reveal', x, y }, local);
            this.drainQueued();
        }
    }
    play(move, base) {
        const result = applyMove(base ?? this.snapshot.game, move);
        if (!result.ok)
            return false;
        const game = result.state;
        if (game.status === 'won' || game.status === 'lost') {
            // The outcome is known *here*, from the local engine, and it is the
            // same engine the server will replay the log through. So it is
            // published immediately and unconditionally: whatever the network is
            // doing, the player learns they won or lost the instant the board
            // says so. The server's verdict — rank, personal best, XP — arrives
            // later as its own update to `result`.
            this.patch({ game, phase: 'finished' });
            // A loss is submitted too, not just a win — the server credits small
            // progress XP for cells safely opened before hitting a mine, so
            // playing well is worth something even when the board isn't cleared.
            this.enqueueSubmission(game);
            return true;
        }
        this.patch({ game });
        return true;
    }
    /**
     * Adds a finished game to the durable queue and starts trying to send it.
     *
     * Nothing waits on this. The queue is the record that the game happened;
     * the network is just how it eventually gets there.
     */
    enqueueSubmission(game) {
        const gameId = this.gameId;
        if (!gameId || this.snapshot.offline)
            return;
        const entry = {
            gameId,
            moves: game.moves.map((move) => ({ ...move })),
            finishedAt: this.now(),
            won: game.status === 'won',
        };
        // Oldest first, and bounded: a very long offline stretch drops the
        // stalest entries rather than growing without limit.
        this.pending = [...this.pending, entry].slice(-PENDING_MAX_ENTRIES);
        this.savePending(this.pending);
        this.scheduleFlush(0);
    }
    clearRetry() {
        if (this.retryTimer !== null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }
    scheduleFlush(attempt) {
        this.clearRetry();
        if (this.pending.length === 0)
            return;
        const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
        if (attempt === 0) {
            void this.flush(0);
            return;
        }
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            void this.flush(attempt);
        }, delay);
    }
    /**
     * Sends the head of the queue.
     *
     * A network failure is not an answer: the game may or may not have been
     * recorded, so the only safe move is to keep the entry and try again. A
     * real HTTP response *is* an answer — accepted, rejected, or no longer
     * submittable — and the entry is dropped either way, because repeating the
     * identical request cannot change it.
     */
    async flush(attempt) {
        if (this.flushing)
            return;
        const entry = this.pending[0];
        const client = this.client;
        if (!entry || !client || !client.hasToken)
            return;
        this.flushing = true;
        // Only the board currently on screen gets to drive the phase; flushing a
        // leftover from an earlier game must not reopen "submitting" over it.
        const isCurrent = entry.gameId === this.gameId;
        if (isCurrent)
            this.patch({ phase: 'submitting' });
        let nextAttempt = null;
        try {
            const result = await client.submitGame(entry.gameId, entry.moves);
            this.drop(entry);
            const changes = { pendingSubmission: this.pending.length > 0 };
            if (isCurrent) {
                changes.phase = 'finished';
                changes.result = result;
                changes.error = null;
            }
            this.patch(changes);
            // Straight on to the next one while the connection is demonstrably up.
            if (this.pending.length > 0)
                nextAttempt = 0;
        }
        catch (error) {
            if (error instanceof ApiError && error.status === 0) {
                // Say so now rather than after the last retry. The old behaviour
                // exhausted every attempt first, which meant the player sat looking
                // at a finished board with no word either way for the better part of
                // a minute. Retries continue in the background from here.
                const changes = { pendingSubmission: true };
                if (isCurrent)
                    changes.phase = 'finished';
                this.patch(changes);
                nextAttempt = attempt + 1;
            }
            else {
                // The server answered. Whatever it said, this entry is finished.
                this.drop(entry);
                const changes = { pendingSubmission: this.pending.length > 0 };
                if (isCurrent) {
                    changes.phase = 'finished';
                    changes.error =
                        error instanceof ApiError && error.isRejection
                            ? `The server would not accept this run: ${error.message}`
                            : describe(error);
                }
                this.patch(changes);
                if (this.pending.length > 0)
                    nextAttempt = 0;
            }
        }
        // Released before scheduling, or the follow-up call would see the guard
        // above still set and quietly stall the queue.
        this.flushing = false;
        if (nextAttempt !== null)
            this.scheduleFlush(nextAttempt);
    }
    drop(entry) {
        this.pending = this.pending.filter((item) => item !== entry);
        this.savePending(this.pending);
    }
    /**
     * Retries a submission that is waiting on the network.
     *
     * Safe to call speculatively — e.g. from the browser's `online` event — since
     * it is a no-op unless something is actually queued.
     */
    retrySubmission() {
        if (this.pending.length === 0)
            return;
        this.scheduleFlush(0);
    }
    /** How many finished games are still waiting to reach the server. */
    get unsentCount() {
        return this.pending.length;
    }
    /**
     * Stops the retry timer. Call when tearing the session down.
     *
     * Deliberately leaves subscribers alone: React's StrictMode mounts,
     * unmounts and remounts in development, and `useSyncExternalStore` manages
     * its own unsubscribe — dropping every listener here would cut the
     * remounted tree off from the session it is still holding.
     */
    dispose() {
        this.clearRetry();
    }
    /** Re-deals at the same difficulty. */
    replay() {
        return this.newGame(this.snapshot.difficulty, this.custom);
    }
}
function describe(error) {
    if (error instanceof ApiError)
        return error.message;
    if (error instanceof Error)
        return error.message;
    return 'Something went wrong.';
}
//# sourceMappingURL=session.js.map